import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { categories, hotRankings } from '@/mocks/comics';

export default function CategoryRankSlider() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 320;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section className="bg-background-100 py-14 md:py-20">
      <div className="px-4 md:px-6 max-w-7xl mx-auto">
        {/* Category filter tabs */}
        <div className="mb-10">
          <div className="flex flex-wrap items-center gap-2">
            {[{ id: 'all', name: '全部分类' }, ...categories].map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeCategory === cat.id
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-50 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Title */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
              分类<span className="text-accent-500">排行</span>
            </h2>
            <p className="text-sm text-foreground-500 mt-2">按分类探索热门漫画榜单</p>
          </div>
          <Link
            to="/category/all?sort=rank"
            className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 bg-foreground-900 text-background-50 rounded-full text-sm font-medium hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
          >
            全部分类
            <i className="ri-arrow-right-s-line"></i>
          </Link>
        </div>

        {/* Horizontal scrollable cards */}
        <div className="relative">
          {/* Left arrow */}
          <button
            onClick={() => scroll('left')}
            className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-background-50 text-foreground-700 rounded-full flex items-center justify-center hover:bg-background-200 transition-all cursor-pointer hidden md:flex"
            aria-label="左滑"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>

          <div
            ref={scrollRef}
            className="flex gap-5 overflow-x-auto scrollbar-hide pb-4 -mx-1 px-1 snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {hotRankings.map((item) => (
              <Link
                key={item.rank}
                to={`/comic/${item.id}`}
                className="flex-shrink-0 w-[220px] md:w-[240px] snap-start cursor-pointer"
              >
                <div className="bg-background-50 rounded-2xl p-5 hover:bg-background-200/50 transition-colors h-full">
                  {/* Rank number */}
                  <div className="flex items-start justify-between mb-4">
                    <span
                      className={`text-4xl md:text-5xl font-bold font-heading ${
                        item.rank <= 3
                          ? 'text-accent-500'
                          : 'text-foreground-300'
                      }`}
                    >
                      {item.rank.toString().padStart(2, '0')}
                    </span>
                    {item.isRising && (
                      <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium">
                        <i className="ri-arrow-up-line"></i>
                        上升
                      </span>
                    )}
                  </div>
                  <h4 className="text-base font-semibold text-foreground-900 font-heading">
                    {item.title}
                  </h4>
                  <p className="text-xs text-foreground-500 mt-1.5">
                    {item.author} · {item.category}
                  </p>
                  <div className="mt-4 flex items-center gap-2">
                    <i className="ri-fire-line text-accent-500 text-sm"></i>
                    <span className="text-sm font-medium text-accent-600">{item.heat}</span>
                    <span className="text-xs text-foreground-400">热度</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Right arrow */}
          <button
            onClick={() => scroll('right')}
            className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-background-50 text-foreground-700 rounded-full flex items-center justify-center hover:bg-background-200 transition-all cursor-pointer hidden md:flex"
            aria-label="右滑"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>

        {/* Mobile view all link */}
        <div className="mt-8 text-center md:hidden">
          <Link
            to="/category/all?sort=rank"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-foreground-900 text-background-50 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer"
          >
            查看完整排行
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}